import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Icon } from "../../components/ui/Icon";
import type { PixelStep } from "../../lib/audienceApi";
import { goalsApi } from "../../lib/goalsApi";
import { useNative } from "../../../shared/phone/media";
import { WizardNav } from "./FlowHead";
import { Spin } from "./Native";
import { andList, cents, dayFullMonth, dollarsTyped, fmt, readGoal, saysWhat, type GoalReading } from "./text";

export interface GoalForm {
  sentence: string;
  budget: string;
  max: string;
  /** The page the result happens on; shown for a Meta goal while it is set up. */
  page: string;
}

export interface GoalValues {
  sentence: string;
  reading: GoalReading & { target: number; by: Date };
  budgetCents: number;
  perResultMaxCents: number;
  /** The page, or null when left empty; undefined when the step does not show it. */
  page?: string | null;
}

const PAGE_HINT = "Use the full page address, starting with https://";

/** The page typed, or null when empty; false when it is not an https address. */
function pageOf(typed: string): string | null | false {
  const t = typed.trim();
  if (!t) return null;
  try {
    return new URL(t).protocol === "https:" ? t : false;
  } catch {
    return false;
  }
}

/** What the form says, when it says enough to save: what counts, how many, by when, the money, and the page when shown. */
export function goalValues(form: GoalForm, withPage = false): GoalValues | null {
  const reading = readGoal(form.sentence);
  const budgetCents = cents(form.budget);
  const perResultMaxCents = cents(form.max);
  const page = withPage ? pageOf(form.page) : undefined;
  if (!saysWhat(reading.what) || reading.target === null || reading.by === null || budgetCents === null || perResultMaxCents === null || page === false) return null;
  return { sentence: form.sentence.trim(), reading: { ...reading, target: reading.target, by: reading.by }, budgetCents, perResultMaxCents, ...(withPage ? { page } : {}) };
}

/** Whether the product's Meta pixel is on the page: looked for a moment after the owner stops typing. */
function PixelLine({ productId, page, native }: { productId: string; page: string | null; native: boolean }) {
  const [step, setStep] = useState<PixelStep | "looking" | null>(null);
  useEffect(() => {
    if (!page) {
      setStep(null);
      return;
    }
    // Why: the answer for an address since changed must not come back.
    let live = true;
    setStep("looking");
    const t = setTimeout(() => {
      goalsApi.pixelCheck(productId, page).then(
        (r) => live && setStep(r.pixel),
        () => live && setStep(null),
      );
    }, 600);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [productId, page]);
  if (!step) return null;
  if (native)
    return (
      <p className={`su-pix${step === "looking" ? "" : step.found ? " ok" : " no"}`} role="status">
        {step === "looking" ? <Spin /> : <Icon name={step.found ? "check" : "info"} />}
        {step === "looking" ? "Looking for your Meta pixel" : step.found ? "Meta pixel found" : (step.error ?? "No Meta pixel on this page yet. Launch waits for it.")}
      </p>
    );
  if (step === "looking")
    return (
      <div className="gs-pix">
        <span className="spin" />
        Looking for your Meta pixel
      </div>
    );
  if (step.found)
    return (
      <div className="gs-pix ok">
        <Icon name="check" />
        Meta pixel found
      </div>
    );
  return (
    <div className="gs-pix no">
      <Icon name="info" />
      {step.error ?? "No Meta pixel on this page yet. Launch waits for it."}
    </div>
  );
}

const EXAMPLE = "For example: 100 people register for the webinar by 30 November";

/** What the goal sentence still lacks; nothing when it says what, how many and by when. */
function goalMissing(r: GoalReading, sentence: string): string | null {
  if (!sentence.trim()) return "Write your goal.";
  const missing = [r.target === null && "how many", r.by === null && "by when", !saysWhat(r.what) && "what should happen"].filter((x): x is string => !!x);
  return missing.length ? `Add ${andList(missing)}.` : null;
}

/** The last day of next month and of the month after, offered when the goal has no date. */
const monthEnds = (today: Date) => [1, 2].map((k) => new Date(today.getFullYear(), today.getMonth() + k + 1, 0));

/** The sentence with the number and the deadline it read marked; each mark glows once as it is found. */
function Marked({ text, marks }: { text: string; marks: [number, number][] }) {
  const out: ReactNode[] = [];
  let at = 0;
  for (const [a, b] of marks) {
    const part = text.slice(a, b);
    out.push(text.slice(at, a), <mark key={/^by\b/i.test(part) ? "by" : "n"}>{part}</mark>);
    at = b;
  }
  out.push(text.slice(at));
  return <>{out}</>;
}

/** What the money buys against the goal, and the budget that would reach it when it falls short. */
function Sum({ r, budget, max, onRaise, native }: { r: GoalReading; budget: number; max: number; onRaise?: (typed: string) => void; native: boolean }) {
  const buys = Math.floor(budget / max);
  const target = r.target;
  const head = (
    <>
      Pays for about <b>{fmt(buys)}</b> {buys === 1 ? "result" : "results"} at ${dollarsTyped(max)} each
    </>
  );
  if (target !== null && buys < target) {
    const needed = dollarsTyped(target * max);
    if (native)
      return (
        <div className="su-sum short">
          <p>
            <Icon name="clock" />
            <span>
              {head}. Your goal is {fmt(target)}.
            </span>
          </p>
          {onRaise && (
            <button type="button" className="su-sug" onClick={() => onRaise(needed)}>
              <Icon name="plus" />
              Raise the budget to ${needed}
            </button>
          )}
        </div>
      );
    return (
      <div className="gs-sum short">
        <span>
          {head}. Your goal is {fmt(target)}.
        </span>
        {onRaise && (
          <button type="button" className="gs-sug" onClick={() => onRaise(needed)}>
            <Icon name="plus" />
            Raise the budget to ${needed}
          </button>
        )}
      </div>
    );
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = r.by ? Math.max(1, Math.round((r.by.getTime() - today.getTime()) / 864e5)) : null;
  if (native)
    return (
      <div className="su-sum">
        <p>
          <Icon name="check" />
          <span>
            {head}
            {r.by && days !== null && (
              <>
                {" "}
                · about <b>${fmt(budget / 100 / days)}</b> a day
              </>
            )}
          </span>
        </p>
      </div>
    );
  return (
    <div className="gs-sum">
      <Icon name="check" />
      <span>
        {head}
        {r.by && days !== null && (
          <>
            {" "}
            · about <b>${fmt(budget / 100 / days)}</b> a day until {dayFullMonth(r.by)}
          </>
        )}
      </span>
    </div>
  );
}

/**
 * Step 1, the goal: the sentence and the money, nothing else. What counts, how many and by when are read from
 * the sentence; what is missing shows once the owner leaves the box or presses Next.
 */
export function GoalStep({
  initial,
  busy,
  onNext,
  page,
  next: nextLabel = "Next: the channel",
  head,
  onValues,
  moneyLocked = false,
}: {
  initial: GoalForm;
  busy: boolean;
  onNext: (values: GoalValues) => void;
  /** Shows where the result happens: the product to look for the pixel in, and the page its profile suggests. */
  page?: { productId: string; suggested: string | null };
  /** The step's main button. */
  next?: string;
  /** On a phone and an iPad: the step's heading, and anything above the form. */
  head?: ReactNode;
  /** Every change, with the values when the form says enough to save; Change the goal keeps them when the person goes back. */
  onValues?: (values: GoalValues | null) => void;
  /** The budget and the most per result show but do not change: an approved goal's budget is an Admin's. */
  moneyLocked?: boolean;
}) {
  const native = useNative();
  const [form, setForm] = useState(initial);
  const [shown, setShown] = useState({ goal: !!initial.sentence.trim(), money: false });
  const goalRef = useRef<HTMLTextAreaElement>(null);
  const budgetRef = useRef<HTMLInputElement>(null);
  const maxRef = useRef<HTMLInputElement>(null);
  const pageRef = useRef<HTMLInputElement>(null);
  const typedPage = useRef(!!initial.page);
  const suggested = page?.suggested ?? null;
  useEffect(() => {
    if (suggested && !typedPage.current) setForm((f) => (f.page ? f : { ...f, page: suggested }));
  }, [suggested]);
  const r = readGoal(form.sentence);
  const values = goalValues(form, !!page);
  const typed = page ? pageOf(form.page) : null;
  const pageMiss = typed === false && (shown.money || form.page.trim().length > 8);
  const set = (patch: Partial<GoalForm>) => {
    const after = { ...form, ...patch };
    setForm(after);
    onValues?.(goalValues(after, !!page));
  };
  const goalMiss = shown.goal ? goalMissing(r, form.sentence) : null;
  const budget = cents(form.budget);
  const max = cents(form.max);
  const budgetMiss = shown.money && budget === null;
  const maxMiss = shown.money && max === null;

  const next = () => {
    if (values) return onNext(values);
    setShown({ goal: true, money: true });
    if (goalMissing(r, form.sentence)) goalRef.current?.focus();
    else if (typed === false) pageRef.current?.focus();
    else if (budget === null) budgetRef.current?.focus();
    else maxRef.current?.focus();
  };
  const addDate = (d: Date) => {
    set({ sentence: `${form.sentence.replace(/[\s.]+$/, "")} by ${dayFullMonth(d)}` });
    goalRef.current?.focus();
  };

  /** Return on a phone's keyboard moves to the next field, and on the last one presses the step's button. */
  const onEnter = (to: (() => void) | null) => (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
    e.preventDefault();
    if (to) to();
    else next();
  };

  if (native)
    return (
      <>
        <div className="pn-stack su-step">
          {head}
          <div className="pn-form su-gform">
            <div className="pn-field">
              <label htmlFor="gT">Your goal</label>
              <div className={`su-sent${goalMiss ? " bad" : ""}`}>
                <div className="su-mirror" aria-hidden="true">
                  {form.sentence ? <Marked text={form.sentence} marks={r.marks} /> : <span className="su-ph">{EXAMPLE}</span>}
                  {"​"}
                </div>
                <textarea
                  ref={goalRef}
                  className="su-ta"
                  id="gT"
                  rows={1}
                  spellCheck={false}
                  enterKeyHint="next"
                  aria-invalid={!!goalMiss}
                  aria-describedby={goalMiss ? "gT-miss" : undefined}
                  value={form.sentence}
                  onChange={(e) => set({ sentence: e.target.value })}
                  onKeyDown={onEnter(() => (page ? pageRef : budgetRef).current?.focus())}
                  onBlur={() => form.sentence.trim() && setShown((s) => ({ ...s, goal: true }))}
                />
              </div>
              {goalMiss && (
                <div className="su-need">
                  <p className="su-err" id="gT-miss">
                    {goalMiss}
                  </p>
                  {form.sentence.trim() && r.by === null && (
                    <div className="su-sugs">
                      {monthEnds(new Date()).map((d) => (
                        <button type="button" className="su-sug" key={d.getTime()} onClick={() => addDate(d)}>
                          <Icon name="plus" />
                          by {dayFullMonth(d)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            {page && (
              <div className="pn-field">
                <label htmlFor="gP">Where it happens</label>
                <div className={`su-url${pageMiss ? " bad" : ""}`}>
                  <Icon name="globe" />
                  <input
                    ref={pageRef}
                    id="gP"
                    value={form.page}
                    inputMode="url"
                    autoComplete="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    placeholder="https://"
                    enterKeyHint="next"
                    aria-invalid={pageMiss}
                    onKeyDown={onEnter(() => budgetRef.current?.focus())}
                    onChange={(e) => {
                      typedPage.current = true;
                      set({ page: e.target.value });
                    }}
                  />
                </div>
                {pageMiss ? <p className="su-err">{PAGE_HINT}</p> : suggested && form.page.trim() === suggested && <p className="su-hint">From your profile.</p>}
                <PixelLine productId={page.productId} page={typed || null} native />
              </div>
            )}
            <div className="su-money">
              <div className="pn-field">
                <label htmlFor="gB">Total budget</label>
                <div className={`su-cash${budgetMiss ? " bad" : ""}`}>
                  <span>$</span>
                  <input
                    ref={budgetRef}
                    id="gB"
                    value={form.budget}
                    readOnly={moneyLocked}
                    inputMode="numeric"
                    enterKeyHint="next"
                    placeholder="3,000"
                    autoComplete="off"
                    aria-invalid={budgetMiss}
                    onKeyDown={onEnter(() => maxRef.current?.focus())}
                    onChange={(e) => set({ budget: e.target.value })}
                  />
                </div>
                {budgetMiss && <p className="su-err">Add the total budget.</p>}
              </div>
              <div className="pn-field">
                <label htmlFor="gM">Most per result</label>
                <div className={`su-cash${maxMiss ? " bad" : ""}`}>
                  <span>$</span>
                  <input
                    ref={maxRef}
                    id="gM"
                    value={form.max}
                    readOnly={moneyLocked}
                    inputMode="numeric"
                    enterKeyHint="go"
                    placeholder="30"
                    autoComplete="off"
                    aria-invalid={maxMiss}
                    onKeyDown={onEnter(null)}
                    onChange={(e) => set({ max: e.target.value })}
                  />
                </div>
                {maxMiss && <p className="su-err">Add the most a result may cost.</p>}
              </div>
            </div>
            {budget !== null && max !== null && <Sum r={r} budget={budget} max={max} onRaise={moneyLocked ? undefined : (raised) => set({ budget: raised })} native />}
          </div>
        </div>
        <WizardNav next={nextLabel} busy={busy} onNext={next} />
      </>
    );

  return (
    <>
      <section className="card gstep">
        <div className="gs-goal">
          <label className="fl" htmlFor="gT">
            Your goal
          </label>
          <div className="gs-sent">
            <div className="gs-mirror" aria-hidden="true">
              {form.sentence ? <Marked text={form.sentence} marks={r.marks} /> : <span className="ph">{EXAMPLE}</span>}
              {"​"}
            </div>
            <textarea
              ref={goalRef}
              className="gs-ta"
              id="gT"
              rows={1}
              spellCheck={false}
              placeholder={EXAMPLE}
              aria-invalid={!!goalMiss}
              aria-describedby={goalMiss ? "gT-miss" : undefined}
              value={form.sentence}
              onChange={(e) => set({ sentence: e.target.value })}
              onBlur={() => form.sentence.trim() && setShown((s) => ({ ...s, goal: true }))}
            />
          </div>
          {goalMiss && (
            <div className="gs-need">
              <span id="gT-miss">{goalMiss}</span>
              {form.sentence.trim() &&
                r.by === null &&
                monthEnds(new Date()).map((d) => (
                  <button type="button" className="gs-sug" key={d.getTime()} onClick={() => addDate(d)}>
                    <Icon name="plus" />
                    by {dayFullMonth(d)}
                  </button>
                ))}
            </div>
          )}
        </div>
        {page && (
          <div className="gs-page">
            <label className="fl" htmlFor="gP">
              Where it happens
            </label>
            <div className="gs-url">
              <Icon name="globe" />
              <input
                ref={pageRef}
                id="gP"
                value={form.page}
                inputMode="url"
                autoComplete="off"
                spellCheck={false}
                placeholder="https://"
                aria-invalid={pageMiss}
                onChange={(e) => {
                  typedPage.current = true;
                  set({ page: e.target.value });
                }}
              />
            </div>
            {pageMiss ? <p className="gmiss">{PAGE_HINT}</p> : suggested && form.page.trim() === suggested && <p className="gs-from">From your profile.</p>}
            <PixelLine productId={page.productId} page={typed || null} native={false} />
          </div>
        )}
        <div className="gs-money">
          <div className="row2">
            <div>
              <label className="fl" htmlFor="gB">
                Total budget
              </label>
              <div className="money">
                <span>$</span>
                <input ref={budgetRef} id="gB" value={form.budget} readOnly={moneyLocked} inputMode="numeric" placeholder="3,000" autoComplete="off" aria-invalid={budgetMiss} onChange={(e) => set({ budget: e.target.value })} />
              </div>
              {budgetMiss && <p className="gmiss">Add the total budget.</p>}
            </div>
            <div>
              <label className="fl" htmlFor="gM">
                Most per result
              </label>
              <div className="money">
                <span>$</span>
                <input ref={maxRef} id="gM" value={form.max} readOnly={moneyLocked} inputMode="numeric" placeholder="30" autoComplete="off" aria-invalid={maxMiss} onChange={(e) => set({ max: e.target.value })} />
              </div>
              {maxMiss && <p className="gmiss">Add the most a result may cost.</p>}
            </div>
          </div>
          {budget !== null && max !== null && <Sum r={r} budget={budget} max={max} onRaise={moneyLocked ? undefined : (raised) => set({ budget: raised })} native={false} />}
        </div>
      </section>
      <WizardNav next={nextLabel} busy={busy} onNext={next} />
    </>
  );
}
